import { SCRIBE_REGULATION_EVENTS } from 'applications/scribe/instrumentations/track';
import cx from 'classnames';
import {
  deliveryPriceReady,
  getPaxDeliveryError,
} from 'common/anytime-booking/delivery';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import ScalableImg from 'common/scalable-img';
import calcPrice from 'common/utils/calc-price';
import formatCurrency from 'common/utils/format-currency';
import { PriceSurgeIcon } from 'components/v2/anytime-booking/common/price-surge-icon';
import { codAdditionalServiceID } from 'constants/anytime-booking-constants';
import { newBookingOverlayType } from 'constants/new-booking-constants';
import { MAX_ADD_ON_NAME_LENGTH } from 'constants/new-bulk-upload-const';
import NewBookingDuck from 'ducks/new-booking-duck';
import NewBulkUploadDuck from 'ducks/new-bulk-upload-duck';
import ScribeDuck from 'ducks/scribe-duck';
import { Icon, Popover, Tooltip } from 'grab-ui';
import { truncateText } from 'helper/anytime-booking';
import { isAreaZoningError, isAreaZoningSoftBlock } from 'helper/error-codes';
import { getDeliveriesInsuranceOptions } from 'helper/insurance';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import _some from 'lodash/some';
import PropTypes from 'prop-types';
import RecipientMSDIcon from 'public/icons/recipient-msd.svg';
import RecipientSingleIcon from 'public/icons/recipient-single.svg';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserCountryCodeFromState } from 'selectors/app-selector';
import { recommendedVehicleTypeSelector } from 'selectors/new-booking-selectors';
import {
  ErrorCircleIcon,
  WarningTriangleIcon,
} from 'views/v2/new-booking-page/quote-panel/warning-triangle-icon';

import { useServiceConfig } from '../common/service-configuration-field';
import styles from './bulk-booking-editor.less';

const CommonFieldButton = ({
  children,
  onClick,
  arrowType,
  tooltip,
  style,
}) => {
  let renderContent;
  if (!children) {
    renderContent = '--';
  } else {
    renderContent = (
      <div className={styles.commonFieldButton} onClick={onClick} style={style}>
        {children}
        {arrowType && <Icon type={arrowType} className={styles.rightIcon} />}
      </div>
    );
  }
  return tooltip ? (
    <Tooltip title={tooltip} placement={'top'}>
      {renderContent}
    </Tooltip>
  ) : (
    renderContent
  );
};

CommonFieldButton.defaultProps = {
  onClick: undefined,
  arrowType: undefined,
  children: undefined,
  tooltip: undefined,
  style: undefined,
};

CommonFieldButton.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
  arrowType: PropTypes.string,
  tooltip: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object,
};

export const NameField = ({ delivery, onToggleExpandDelivery }) => {
  const dropOffs = _get(delivery, ['detail', 'dropOffs']);
  if (!dropOffs) {
    return '--';
  }

  const count = dropOffs.length;
  if (count === 1) {
    return (
      <div className={styles.name}>
        <div className={styles.icon}>
          <img alt="" src={RecipientSingleIcon} />
        </div>
        {truncateText(_get(dropOffs[0], 'name'))}
      </div>
    );
  }

  return (
    <div
      className={cx(styles.name, styles.expandable)}
      onClick={onToggleExpandDelivery(delivery.id)}
    >
      <div className={styles.icon}>
        <img alt="" src={RecipientMSDIcon} />
      </div>
      {I18n.t(
        'bulkUploadRevamp.step3.deliveryTable.field.recipientCount.other',
        { count },
      )}
    </div>
  );
};

NameField.defaultProps = {
  onToggleExpandDelivery: undefined,
};

NameField.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
  onToggleExpandDelivery: PropTypes.func.isRequired,
};

export const AddressField = ({ delivery, onToggleExpandDelivery }) => {
  const dropOffs = _get(delivery, ['detail', 'dropOffs']);
  if (!dropOffs) {
    return '--';
  }

  if (dropOffs.length === 1) {
    return (
      <div className={styles.address}>
        {_get(dropOffs[0], 'poi.address.combined_address')}
      </div>
    );
  }

  return (
    <CommonFieldButton
      onClick={onToggleExpandDelivery(delivery.id)}
      arrowType={'down'}
    >
      {I18n.t('bulkUploadRevamp.step3.deliveryTable.field.multipleAddresses')}
    </CommonFieldButton>
  );
};

AddressField.defaultProps = {
  onToggleExpandDelivery: undefined,
};

AddressField.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
  onToggleExpandDelivery: PropTypes.func,
};

export const DeliveryOptionsField = ({ delivery }) => {
  const countryCode = useSelector(getUserCountryCodeFromState);
  const { serviceType, time } = useServiceConfig(delivery, countryCode);
  const scheduledPickupSlot = _get(
    delivery,
    'detail.configuration.scheduledPickupSlot',
  );
  const timeSlotsLength = _get(
    delivery,
    'config.scheduleInfo.timeSlots.length',
    0,
  );
  const dispatch = useDispatch();
  const onClick = useCallback(
    () => {
      dispatch(
        NewBookingDuck.creators.openBulkEditOverlay(
          [delivery.id],
          newBookingOverlayType.serviceConfiguration,
        ),
      );
    },
    [dispatch, delivery.id],
  );

  if (!serviceType) {
    return (
      <CommonFieldButton onClick={onClick} arrowType={'right'}>
        {I18n.t(
          'phrase.geWebBulkUpload.express_web_bulk_upload_button_add_delivery_option_happypath',
        )}
      </CommonFieldButton>
    );
  }
  return (
    <CommonFieldButton onClick={onClick} arrowType={'right'}>
      <ScalableImg
        alt=""
        src={_get(serviceType, 'icon')}
        className={styles.leftIcon}
      />
      {scheduledPickupSlot === undefined && timeSlotsLength > 0
        ? I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_button_add_pickup_time_happypath',
          )
        : _get(time, 'pickUpTime')}
    </CommonFieldButton>
  );
};

DeliveryOptionsField.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
};

export const VehicleTypeField = ({ delivery }) => {
  const vehicleTypes = _get(delivery, 'config.vehicleTypes', []);
  const vehicleType = _get(delivery, 'detail.configuration.vehicleType');
  const recommendedVehicleType = recommendedVehicleTypeSelector(delivery);
  const value = vehicleType || recommendedVehicleType;
  const vehicleDetail = vehicleTypes.find(vt => vt.id === value);
  const icon = _get(vehicleDetail, 'icon', '');
  const name = _get(vehicleDetail, 'name');
  const dispatch = useDispatch();
  const onClick = useCallback(
    () => {
      dispatch(
        NewBookingDuck.creators.openBulkEditOverlay(
          [delivery.id],
          newBookingOverlayType.vehicleType,
        ),
      );
    },
    [dispatch, delivery.id],
  );

  return (
    <CommonFieldButton
      onClick={onClick}
      arrowType={'right'}
      style={{ minWidth: 90 }}
    >
      {name && [
        <ScalableImg
          key={'img'}
          alt=""
          src={icon}
          className={styles.leftIcon}
        />,
        name,
      ]}
    </CommonFieldButton>
  );
};

VehicleTypeField.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
};

export const DeliveryGuaranteeField = ({ delivery }) => {
  const dispatch = useDispatch();
  const onClick = useCallback(
    () => {
      dispatch(
        NewBookingDuck.creators.openBulkEditOverlay(
          [delivery.id],
          newBookingOverlayType.deliveryGuarantee,
        ),
      );
    },
    [dispatch, delivery.id],
  );
  const isMSD = _get(delivery, 'detail.dropOffs.length', 0) > 1;
  const { totalSelected, optionsCount, selectedInsuranceOption } = useMemo(
    () => getDeliveriesInsuranceOptions([delivery]),
    [isMSD, delivery],
  );
  if (!totalSelected) {
    return (
      <CommonFieldButton onClick={onClick} arrowType={'right'}>
        --
      </CommonFieldButton>
    );
  }

  if (selectedInsuranceOption) {
    return (
      <CommonFieldButton onClick={onClick} arrowType={'right'}>
        {(selectedInsuranceOption && selectedInsuranceOption.title) || '--'}
      </CommonFieldButton>
    );
  }

  return (
    <CommonFieldButton onClick={onClick} arrowType={'right'}>
      {Object.entries(optionsCount)
        .map(([optionTitle, count]) => `${optionTitle} x ${count}`)
        .join(', ')}
    </CommonFieldButton>
  );
};

DeliveryGuaranteeField.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
};

export const AddOnsField = ({ delivery }) => {
  const selectedAST = _get(
    delivery,
    'detail.configuration.additionalServices',
  ).filter(addOn => addOn.id !== codAdditionalServiceID);
  const validAST = _get(delivery, 'config.additionalServices', []).filter(
    addOn => addOn.id !== codAdditionalServiceID,
  );
  const haveSomeAvailableAddOns = _some(
    validAST,
    addOn => !_get(addOn, 'errorMessage'),
  );

  const disabled =
    !(
      _get(delivery, 'detail.configuration.serviceType') &&
      _get(delivery, 'detail.configuration.specialServiceType')
    ) || !haveSomeAvailableAddOns;

  const dispatch = useDispatch();
  const onClick = useCallback(
    () => {
      dispatch(
        NewBookingDuck.creators.openBulkEditOverlay(
          [delivery.id],
          newBookingOverlayType.addOn,
        ),
      );
    },
    [dispatch, delivery.id],
  );

  let name;
  if (selectedAST.length > 1) {
    name = I18n.t('bulkUploadRevamp.step3.addOnCount', {
      name: truncateText(_get(selectedAST[0], 'name'), MAX_ADD_ON_NAME_LENGTH),
      num: selectedAST.length - 1,
    });
  } else if (selectedAST.length === 1) {
    name = truncateText(_get(selectedAST[0], 'name'), MAX_ADD_ON_NAME_LENGTH);
  } else {
    name = '--';
  }

  return (
    <CommonFieldButton
      onClick={onClick}
      arrowType={'right'}
      tooltip={
        !haveSomeAvailableAddOns
          ? I18n.t('bulkUploadRevamp.step3.tooltip.addon')
          : undefined
      }
    >
      {disabled ? undefined : name}
    </CommonFieldButton>
  );
};

AddOnsField.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
};

export const PriceField = ({ delivery }) => {
  const dispatch = useDispatch();
  const onDelete = useCallback(
    () => {
      dispatch(NewBulkUploadDuck.creators.deleteDelivery(delivery.id));
    },
    [dispatch, delivery.id],
  );
  const onRefresh = useCallback(
    () => {
      dispatch(NewBookingDuck.creators.batchFetchDeliveryQuotes([delivery.id]));
    },
    [dispatch, delivery.id],
  );

  const paxDeliveryError = getPaxDeliveryError(delivery, true);
  const { title, errorCode, message } = paxDeliveryError || {};
  const isEVError = isAreaZoningError(errorCode);
  const isSoftBlock = isAreaZoningSoftBlock(errorCode);
  const priceReady = deliveryPriceReady(delivery);

  useEffect(
    () => {
      if (isEVError) {
        dispatch(
          ScribeDuck.creators.scribeEVRegulationEvent({
            eventName:
              SCRIBE_REGULATION_EVENTS.EV_REGULATION_PRICE_BREAKDOWN_ERROR_VIEW,
            errorCode,
            title: message || title,
          }),
        );
      }
    },
    [dispatch, isEVError, errorCode, title, message],
  );

  const onTooltipVisibleChange = useCallback(
    visible => {
      if (visible && isEVError) {
        dispatch(
          ScribeDuck.creators.scribeEVRegulationEvent({
            eventName:
              SCRIBE_REGULATION_EVENTS.EV_REGULATION_PRICE_BREAKDOWN_TOOLTIP_VIEW,
            errorCode,
            title: message || title,
          }),
        );
      }
    },
    [dispatch, isEVError, errorCode, title, message],
  );

  const onDeleteWithTracking = useCallback(
    () => {
      if (isEVError) {
        dispatch(
          ScribeDuck.creators.scribeEVRegulationEvent({
            eventName:
              SCRIBE_REGULATION_EVENTS.EV_REGULATION_PRICE_BREAKDOWN_TOOLTIP_DELETE,
            errorCode,
            title: message || title,
          }),
        );
      }
      onDelete();
    },
    [dispatch, isEVError, errorCode, title, onDelete, message],
  );

  const onRefreshWithTracking = useCallback(
    () => {
      if (isEVError) {
        dispatch(
          ScribeDuck.creators.scribeEVRegulationEvent({
            eventName:
              SCRIBE_REGULATION_EVENTS.EV_REGULATION_PRICE_BREAKDOWN_TOOLTIP_REFRESH,
            errorCode,
            title: message || title,
          }),
        );
      }
      onRefresh();
    },
    [dispatch, isEVError, errorCode, title, onRefresh, message],
  );

  // Render soft block warning icon component
  const softBlockWarning = isSoftBlock && (
    <Popover
      trigger={'hover'}
      onVisibleChange={onTooltipVisibleChange}
      content={
        <div className={styles.deliveryErrorPopOver}>
          <h4 className={styles.message}>{title || 'Error'}</h4>
          <div className={styles.actions}>
            <a onClick={onDeleteWithTracking}>
              {I18n.t('v2.homepage.uploadOrder.reviewTable.action.delete', {
                defaultValue: 'Delete',
              })}
            </a>
            <a onClick={onRefreshWithTracking}>
              {I18n.t('v2.homepage.uploadOrder.reviewTable.action.refresh', {
                defaultValue: 'Refresh',
              })}
            </a>
          </div>
        </div>
      }
    >
      <div className={styles.deliveryError}>
        <WarningTriangleIcon />
      </div>
    </Popover>
  );

  // For hard block errors or other non-soft-block errors, show error icon only
  if (paxDeliveryError && !isSoftBlock) {
    return (
      <Popover
        trigger={'hover'}
        onVisibleChange={onTooltipVisibleChange}
        content={
          <div className={styles.deliveryErrorPopOver}>
            <h4 className={styles.message}>
              {isEVError ? title || 'Error' : message}
            </h4>
            <div className={styles.actions}>
              <a onClick={onDeleteWithTracking}>
                {I18n.t('v2.homepage.uploadOrder.reviewTable.action.delete', {
                  defaultValue: 'Delete',
                })}
              </a>
              <a onClick={onRefreshWithTracking}>
                {I18n.t('v2.homepage.uploadOrder.reviewTable.action.refresh', {
                  defaultValue: 'Refresh',
                })}
              </a>
            </div>
          </div>
        }
      >
        <div className={styles.deliveryError}>
          {isEVError ? <ErrorCircleIcon /> : <Icon type="info-circle" />}
        </div>
      </Popover>
    );
  }

  if (priceReady) {
    // use originalFare in step 3
    const quote = _get(delivery, ['quote']);
    const amount = _get(quote, ['originalFare', 'lowerBound'], 0);
    const currency = _get(quote, 'currency');

    const code = _get(currency, 'code');
    return (
      <div className={styles.price}>
        <span>
          {formatCurrency({ code, amount: calcPrice(amount, currency) })}
        </span>
        {softBlockWarning}
        <PriceSurgeIcon
          className={styles.priceSurgeIcon}
          priceSurgeInfo={quote}
        />
      </div>
    );
  }

  // If no price, show -- with warning icon if soft block
  return (
    <div className={styles.price}>
      <span>--</span>
      {softBlockWarning}
    </div>
  );
};

PriceField.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
};

export const SizeAndWeightField = ({ delivery }) => {
  // S • 10 kg

  const totalWeight = _get(delivery, 'detail.dropOffs', []).reduce(
    (partialWeight, step) => partialWeight + _get(step, 'itemWeight', 0),
    0,
  );

  return (
    <div>
      {_get(delivery, ['detail', 'parcelTotalSize'])} • {totalWeight} kg
    </div>
  );
};

SizeAndWeightField.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
};
